import { Button, Card, Modal, Table, Form } from "react-bootstrap";
import { useState, useContext, useEffect } from "react";
import useCallApi from "../../../hooks/useCallApi";
import callAPI from "../../../helpers/callAPI";
import { SystemMessageContext } from "../../../context/SystemMessageContext";

const OtherIO = ({ campaignId }) => {
  const { showSystemMessage } = useContext(SystemMessageContext);
  const { data, dataLoaded, refetch } = useCallApi(`campaign/${campaignId}/otherIO`, 'GET', {}, [campaignId]);
  const [modalMode, setModalMode] = useState(false);
  const [isContractAvailable, setIsContractAvailable] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docType, setDocType] = useState('');
  const [docLink, setDocLink] = useState('');
  const [docNotes, setDocNotes] = useState('');
  const [docId, setDocId] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const resetForm = () => {
    setDocType('');
    setDocLink('');
    setDocNotes('');
    setDocId(null);
    setSelectedDoc(null);
    setModalMode(false);
  };

  const handleEditClick = (doc) => {
    setSelectedDoc(doc);
    setDocType(doc.type);
    setDocLink(doc.url);
    setDocNotes(doc.notes);
    setDocId(doc.docId);
    setModalMode('edit');
  };

  const handleDelete = async (id) => {
    if (pendingDeleteId !== id) {
      setPendingDeleteId(id);
      return;
    }

    setPendingDeleteId(null);
    try {
      const response = await callAPI(`campaign/${campaignId}/otherIO`, 'DELETE', {
        docId: id
      });
      if (response.success) {
        showSystemMessage('success', 'Document deleted successfully');
        refetch();
      } else {
        showSystemMessage('error', 'Failed to delete document');
      }
    } catch (error) {
      showSystemMessage('error', 'Failed to delete document');
    }
  };

  const checkContractAvailability = async () => {
    try {
      const mbToken = localStorage.getItem('adm-auth-token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/docusignConnect/${campaignId}/downloadInsertionOrder`, {
        method: 'HEAD',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-app-token': mbToken,
        }
      });
      setIsContractAvailable(response.status === 200);
    } catch (error) {
      setIsContractAvailable(false);
    }
  };

  useEffect(() => {
    checkContractAvailability();
  }, [campaignId]);

  const handleDocumentDownload = async () => {
    try {

      const mbToken = localStorage.getItem('adm-auth-token')

      const response = await fetch(`${process.env.REACT_APP_API_URL}/docusignConnect/${campaignId}/downloadInsertionOrder`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-app-token': mbToken,
        }
      });

      if (response.status !== 200) {
        showSystemMessage('error', 'Signed IO does not exist.');
        return;
      }

      const campaignResponse = await callAPI(`campaign/${campaignId}`, 'GET')

      let blob = await response.blob()

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${campaignResponse.campaign.advertiser.name} - ${campaignResponse.campaign.name} - Insertion Order.pdf`; // This will override the Content-Disposition name
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading document:', error);
      showSystemMessage('Error downloading document', 'error');
    }
  };

  const formatUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
  };

  const handleSubmit = async () => {
    try {
      if (modalMode === 'edit') {
        await callAPI(`campaign/${campaignId}/otherIO`, 'PUT', {
          type: docType,
          url: formatUrl(docLink),
          notes: docNotes,
          docId: docId
        });
        showSystemMessage('success', 'Document updated successfully');
      } else {
        await callAPI(`campaign/${campaignId}/otherIO`, 'POST', {
          type: docType,
          url: formatUrl(docLink),
          notes: docNotes
        });
        showSystemMessage('success', 'Document added successfully');
      }
      setModalMode(false);
      refetch();
      resetForm();
    } catch (error) {
      showSystemMessage('error', `Failed to ${modalMode} document`);
    }
  };

  return (
    <>
      <div className="card mt-2 mb-3">
          <div className="card-header d-flex justify-content-between align-items-center">
              <div>IOs and Rate Adjustments</div>
              <div>                  
                  <button className="btn btn-warning btn-sm" onClick={() => setModalMode('create')}>Add</button>
              </div>
          </div>
          <table className="table table-striped table-bordered table-hover mb-0">
            <thead>
              <tr>
                <th>Type</th>
                <th>Notes</th>
                <th>Date Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {
                isContractAvailable && (
                  <tr>
                    <td>
                      {<span className="text-primary" style={{ cursor: 'pointer' }} onClick={handleDocumentDownload} > Docusign Linked IO </span>}
                    </td>
                    <td colSpan={3}>Added via Admin/Docusign</td>
                  </tr>
                )
              }
              {
                dataLoaded && data.success && data.data.length > 0 ? (
                  data.data.map((doc, index) => (
                    <tr key={index}>
                      <td>
                        <a href={formatUrl(doc.url)} target="_blank" rel="noopener noreferrer">
                          {doc.type}
                        </a>
                      </td>
                      <td>{doc.notes}</td>
                      <td>{new Date(doc.db_date).toLocaleDateString()}</td>
                      <td>
                        <Button variant="link" onClick={() => handleEditClick(doc)}>
                          Edit
                        </Button>
                        <Button 
                          size="sm"
                          variant={pendingDeleteId === doc.docId ? "danger" : "outline-danger"}
                          onClick={async () => handleDelete(doc.docId)}
                        >
                          {pendingDeleteId === doc.docId ? "Confirm Delete" : "Delete"}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center">No documents available.</td>
                  </tr>
                )}
            </tbody>
          </table>
      </div>

      <Modal
        show={modalMode !== false}
        onHide={() => {
          setModalMode(false);
          resetForm();
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'create' ? 'Add Document' : 'Edit Document'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center w-100 mb-2 mt-3">
            <Form.Label>Type</Form.Label>
            <div className="w-75">
              <Form.Control
                className="mb-3"
                as="select"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
              >
                <option value="">Select Type</option>
                <option value="IO">IO</option>
                <option value="Rate Adjustment">Rate Adjustment</option>
                <option value="Agreement">Agreement</option>
                <option value="Other">Other</option>
              </Form.Control>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center w-100 mb-2">
            <Form.Label>Link</Form.Label>
            <div className="w-75">
              <Form.Control
                className="mb-3"
                type="url"
                placeholder="Enter document link"
                value={docLink}
                onChange={(e) => setDocLink(e.target.value)}
              />
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center w-100 mb-2">
            <Form.Label>Notes</Form.Label>
            <div className="w-75">
              <Form.Control
                className="mb-3"
                type="text"
                rows={3}
                value={docNotes}
                onChange={(e) => setDocNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={resetForm} className="mr-2">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!docType || !docLink}
            >
              {modalMode === 'create' ? 'Save' : 'Update'}
            </Button>
          </div>
        </Modal.Body>
      </Modal >
    </>
  );
}

export default OtherIO;